import React, { memo, useEffect, useMemo, useState } from 'react';
import {
  Stack,
  TextField,
  MenuItem,
  Typography,
  Checkbox,
  FormGroup,
  FormControlLabel,
  CircularProgress,
  Divider
} from 'investira.react.components';
import services from '../../../services';
import { CARTEIRA_TIPO } from '../../../enums';

/**
 * Tipos de carteira disponíveis para filtro.
 * P = Planejamento, T = Terceiros, G = Gestão, S = Sociedade
 * @constant {string[]}
 */
const TIPOS_DISPONIVEIS = ['P', 'T', 'G', 'S'];

/**
 * Extrai a chave de identificação de uma carteira (ID).
 * @param {Object|string} item - Carteira ou ID da carteira
 * @returns {string} ID da carteira como string
 */
function getCarteiraKey(item) {
  if (typeof item === 'object' && item !== null) {
    return String(item.carteira_id);
  }

  return String(item);
}

/**
 * Componente de seleção e busca de carteiras com filtros avançados.
 * Permite filtrar por nome/ID, favorito, tipo (P, T, G, S) e estado de seleção.
 *
 * @param {Array} value - Array de IDs ou objetos de carteiras selecionadas
 * @param {Function} onChange - Callback chamado quando a seleção muda, recebe array de carteiras selecionadas
 * @param {boolean} [disabled=false] - Desabilita toda a interface
 *
 * @example
 * <PorfolioCarteiraSearch
 *   value={selectedWallets}
 *   onChange={setSelectedWallets}
 *   disabled={false}
 * />
 */
const PorfolioCarteiraSearch = memo(props => {
  const { value, onChange, disabled = false } = props;

  /**
   * Estados do componente:
   * - isLoading: Indica se está carregando as carteiras
   * - error: Mensagem de erro se a API falhar
   * - carteiras: Lista completa de carteiras retornadas pela API
   * - pesquisa: Texto de busca por nome ou ID
   * - favorito: Filtro de favorito (all/1/0)
   * - tiposSelecionados: Checkboxes marcados dos tipos P, T, G, S
   * - filtroSelecionadas: Filtro de seleção (all/selected/unselected)
   * - selectedCarteiras: Array de carteiras atualmente selecionadas
   */
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [carteiras, setCarteiras] = useState([]);
  const [pesquisa, setPesquisa] = useState('');
  const [favorito, setFavorito] = useState('all');
  const [tiposSelecionados, setTiposSelecionados] = useState(['P']);
  const [filtroSelecionadas, setFiltroSelecionadas] = useState('all');
  const [selectedCarteiras, setSelectedCarteiras] = useState(Array.isArray(value) ? value : []);

  /**
   * Sincroniza o estado interno selectedCarteiras com a prop value quando esta muda.
   */
  useEffect(() => {
    if (Array.isArray(value)) {
      setSelectedCarteiras(value);
    }
  }, [value]);

  /**
   * Cria um Set com os IDs das carteiras selecionadas para rápido acesso O(1).
   * @returns {Set<string>} Set de IDs selecionados
   */
  const selectedIds = useMemo(() => {
    if (!Array.isArray(selectedCarteiras)) {
      return new Set();
    }

    return new Set(selectedCarteiras.map(item => getCarteiraKey(item)));
  }, [selectedCarteiras]);

  /**
   * Efeito para carregar as carteiras da API ao montar o componente.
   * Busca todas as carteiras ordenadas por favorito e nome.
   */
  useEffect(() => {
    setIsLoading(true);
    setError(null);

    services.carteiras.list(
      {
        sort: 'favorito desc, carteira',
        page: 1,
        size: 9999
      },
      rRes => {
        setCarteiras(Object.values(rRes?.data || {}));
      },
      () => {
        setError('Não foi possível carregar as carteiras');
      },
      () => {
        setIsLoading(false);
      }
    );
  }, []);

  /**
   * Filtra as carteiras baseado nos critérios: tipo, favorito e texto de busca.
   * @returns {Array} Array de carteiras filtradas
   */
  const carteirasFiltradas = useMemo(() => {
    const termo = pesquisa.trim().toLowerCase();

    return carteiras.filter(item => {
      const tipoMatch = tiposSelecionados.includes(item.carteira_tipo_id);

      const favoritoMatch = favorito === 'all' || Number(item.favorito) === Number(favorito);

      const textoBase = `${item.carteira || ''} ${item.carteira_seuid || ''}`.toLowerCase();
      const textoMatch = !termo || textoBase.includes(termo);

      return tipoMatch && favoritoMatch && textoMatch;
    });
  }, [carteiras, tiposSelecionados, favorito, pesquisa]);

  /**
   * Aplica filtro adicional baseado no estado de seleção das carteiras.
   * @returns {Array} Array de carteiras visíveis após todos os filtros
   */
  const carteirasVisiveis = useMemo(() => {
    if (filtroSelecionadas === 'all') {
      return carteirasFiltradas;
    }

    return carteirasFiltradas.filter(carteira => {
      const isSelecionada = selectedIds.has(getCarteiraKey(carteira));

      return filtroSelecionadas === 'selected' ? isSelecionada : !isSelecionada;
    });
  }, [carteirasFiltradas, filtroSelecionadas, selectedIds]);

  /**
   * Alterna a seleção de um tipo de carteira (P, T, G, S).
   * @param {string} tipoId - ID do tipo de carteira
   */
  const handleTipoChange = tipoId => {
    setTiposSelecionados(prev => {
      if (prev.includes(tipoId)) {
        return prev.filter(item => item !== tipoId);
      }

      return [...prev, tipoId];
    });
  };

  /**
   * Alterna a seleção de uma carteira (adiciona ou remove do array).
   * Chama onChange se provided, senão atualiza estado interno.
   * @param {Object|string} carteira - Carteira ou ID a ser toggleado
   */
  const handleToggleCarteira = carteira => {
    const carteiraId = getCarteiraKey(carteira);

    const next = selectedIds.has(carteiraId)
      ? (Array.isArray(selectedCarteiras) ? selectedCarteiras : []).filter(item => {
          return getCarteiraKey(item) !== carteiraId;
        })
      : [...(Array.isArray(selectedCarteiras) ? selectedCarteiras : []), carteira];

    !onChange && setSelectedCarteiras(next);
    onChange && onChange(next);
  };

  return (
    <Stack spacing={2}>
      <Stack>
        <Typography variant="h6" color="textPrimary">
          Selecione as carteiras
        </Typography>
      </Stack>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <TextField
          variant="outlined"
          size="small"
          fullWidth
          label="Filtrar por nome ou ID"
          placeholder="Ex: Carteira Alpha ou CA1"
          value={pesquisa}
          onChange={event => setPesquisa(event.target.value)}
          disabled={disabled || isLoading}
        />

        <TextField
          select
          variant="outlined"
          size="small"
          label="Favorito"
          value={favorito}
          onChange={event => setFavorito(event.target.value)}
          disabled={disabled || isLoading}
          sx={{ minWidth: { xs: '100%', md: 180 } }}>
          <MenuItem value="all">Todos</MenuItem>
          <MenuItem value="1">Somente favoritos</MenuItem>
          <MenuItem value="0">Somente não favoritos</MenuItem>
        </TextField>

        <TextField
          select
          label="Seleção"
          size="small"
          variant="outlined"
          value={filtroSelecionadas}
          onChange={event => setFiltroSelecionadas(event.target.value)}
          disabled={disabled}
          sx={{ ml: 'auto', minWidth: 220 }}>
          <MenuItem value="all">Todas</MenuItem>
          <MenuItem value="selected">Todas selecionadas</MenuItem>
          <MenuItem value="unselected">Todas não selecionadas</MenuItem>
        </TextField>
      </Stack>

      <FormGroup row>
        {TIPOS_DISPONIVEIS.map(tipoId => (
          <FormControlLabel
            key={tipoId}
            control={
              <Checkbox
                checked={tiposSelecionados.includes(tipoId)}
                onChange={() => handleTipoChange(tipoId)}
                disabled={disabled || isLoading}
              />
            }
            label={
              <Typography variant="body2" color="textPrimary">
                {CARTEIRA_TIPO[tipoId]?.label || tipoId}
              </Typography>
            }
          />
        ))}
      </FormGroup>

      <Divider />

      {isLoading && (
        <Stack alignItems="center" py={2}>
          <CircularProgress size={24} />
        </Stack>
      )}

      {!isLoading && error && (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      )}

      {!isLoading && !error && (
        <Stack spacing={1} sx={{ maxHeight: 320, overflow: 'auto' }}>
          <Stack direction="row" gap={2} justifyContent="space-between" alignItems="center">
            <Stack direction="row" gap={2}>
              <Typography variant="caption" color="textSecondary">
                {`${carteirasVisiveis.length} carteira(s) encontrada(s)`}
              </Typography>
              <Typography variant="caption" color="textPrimary" sx={{ fontWeight: 500 }}>
                {`${selectedIds.size} carteira(s) selecionada(s)`}
              </Typography>
            </Stack>
          </Stack>

          {carteirasVisiveis.map(carteira => {
            const carteiraId = getCarteiraKey(carteira);

            return (
              <Stack
                key={carteiraId}
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  cursor: disabled ? 'default' : 'pointer',
                  '&:hover': {
                    backgroundColor: disabled ? 'transparent' : 'background.main'
                  }
                }}
                onClick={() => !disabled && handleToggleCarteira(carteira)}>
                <Checkbox
                  checked={selectedIds.has(carteiraId)}
                  disabled={disabled}
                  onClick={event => event.stopPropagation()}
                  onChange={() => handleToggleCarteira(carteira)}
                />
                <Stack>
                  <Typography variant="body2" color="textPrimary">
                    {carteira.carteira || carteira.carteira_seuid}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {`${carteira.carteira_seuid} • ${
                      CARTEIRA_TIPO[carteira.carteira_tipo_id]?.label || carteira.carteira_tipo_id
                    }`}
                  </Typography>
                </Stack>
              </Stack>
            );
          })}

          {!carteirasVisiveis.length && (
            <Typography variant="body2" color="textSecondary">
              Nenhuma carteira encontrada para os filtros informados.
            </Typography>
          )}
        </Stack>
      )}
      <Divider />
    </Stack>
  );
});

PorfolioCarteiraSearch.displayName = 'PorfolioCarteiraSearch';

export default PorfolioCarteiraSearch;
